import PropTypes from 'prop-types';
import type { ReactNode } from 'react';
import React from 'react';
import styled from 'styled-components';
import type { Spacing } from '../../Spacing';
import { Space } from '../../Spacing';

type SpacingType = (typeof Spacing)[number];

export type BoxProps = {
  gap?: SpacingType;
  padding?: SpacingType;
  paddingTop?: SpacingType;
  paddingLeft?: SpacingType;
  paddingRight?: SpacingType;
  paddingBottom?: SpacingType;
  p?: SpacingType;
  pt?: SpacingType;
  pb?: SpacingType;
  pl?: SpacingType;
  pr?: SpacingType;
  margin?: SpacingType;
  m?: SpacingType;
  mt?: SpacingType;
  mb?: SpacingType;
  ml?: SpacingType;
  mr?: SpacingType;
  marginTop?: SpacingType;
  marginLeft?: SpacingType;
  marginRight?: SpacingType;
  marginBottom?: SpacingType;
  display?: React.CSSProperties['display'];
  children?: ReactNode;
  alignItems?: React.CSSProperties['alignItems'];
  width?: React.CSSProperties['width'];
  w?: React.CSSProperties['width'];
};

const propTypes = {
  gap: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  paddingTop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  paddingLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  paddingRight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  paddingBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  p: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pb: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pl: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pr: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  m: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ml: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mr: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  marginTop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  marginLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  marginRight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  marginBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  display: PropTypes.string,
  alignItems: PropTypes.string,
  width: PropTypes.string,
  w: PropTypes.string,
};

const spacingKeys = [
  'gap',
  'p',
  'pt',
  'pb',
  'pl',
  'pr',
  'm',
  'mt',
  'mb',
  'ml',
  'mr',
  'padding',
  'paddingTop',
  'paddingBottom',
  'paddingLeft',
  'paddingRight',
  'margin',
  'marginTop',
  'marginBottom',
  'marginLeft',
  'marginRight',
];

const shortForm = {
  p: 'padding',
  pt: 'paddingTop',
  pb: 'paddingBottom',
  pl: 'paddingLeft',
  pr: 'paddingRight',
  m: 'margin',
  mt: 'marginTop',
  mb: 'marginBottom',
  ml: 'marginLeft',
  mr: 'marginRight',
  w: 'width',
};

type NormalizerKeyValue<T> = [k: string, v: T];
type Normalizer<T> = (kv: NormalizerKeyValue<T>) => NormalizerKeyValue<T>;

const toSpaceValue: Normalizer<any> = ([k, v]) => {
  if (k in spacingKeys) {
    return [k, typeof v === 'string' ? v : Space(v)];
  }
  return [k, v];
};

const toLongForm: Normalizer<any> = ([k, v]: [k: string, v: any]) => [
  // @ts-ignore TS7053
  shortForm[k] || k,
  v,
];

const toSnakeKey: Normalizer<any> = ([k, v]: [k: string, v: any]) => [
  k.replace(/[A-Z]/g, (c: string) => `-${c.toLowerCase()}`),
  v,
];

const normalize =
  (...normalizers: Normalizer<any>[]) =>
  ([k, v]: [k: string, v: any]) =>
    normalizers.reduce(([nK, nV], normalizer) => normalizer([nK, nV]), [k, v]);

export const pickBoxProps = <T extends object>(props: T) => {
  const propKeys = Object.keys(propTypes);
  return Object.entries(props).reduce((acc, [k, v]) => {
    if (propKeys.includes(k)) {
      return {
        ...acc,
        [k]: v,
      };
    }

    return acc;
  }, {});
};

const Container = styled.div((props) =>
  // @ts-ignore TS7053
  Object.entries(pickBoxProps(props))
    .map(normalize(toLongForm, toSpaceValue, toSnakeKey))
    .reduce(
      (acc, [k, v]) => ({
        ...acc,
        [k]: v,
      }),
      {}
    )
);

const Box = (props: BoxProps) => {
  const { children, ...rest } = props;

  return <Container {...rest}>{children}</Container>;
};

Box.propTypes = propTypes;

export default Box;
